import Debug from 'debug'
import Cookies from 'universal-cookie'
import z from 'zod'
import type { UseQueryOptions } from '@tanstack/react-query'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'

import { queryClient } from '@jotta/query'
import { AppError } from '@jotta/types/AppError'
import { asTrilean } from '@jotta/utils/trilean'

const debug = Debug('jotta:auth')
const cookies = new Cookies()
const AuthStatusKey = ['authstatus']
/** Renew a token this close to expiry before sending it. */
const refreshBufferMillis = 30_000
/**
 * A status this recent is reused instead of asking the server again, so
 * logged-out pages and tokens the server won't renew yet don't cost one
 * status call per request (master debounced for 4 s).
 */
const statusReuseMillis = 5_000
/** When the timed refresh fails (e.g. still offline after sleep), try again. */
const timedRefreshRetryMillis = 30_000
/**
 * Every request waits for a pending status check, so one that hangs during a
 * network change must not hold them all: give up like a call would.
 */
const authRequestTimeoutMillis = 10_000

function authFetch(path: '/api/status' | '/api/refresh') {
  return fetch(path, { signal: AbortSignal.timeout(authRequestTimeoutMillis) })
}

const AuthStatusSchema = z.object({
  authenticated: z.boolean(),
  refreshable: z.boolean(),
  username: z.string().nullable(),
  expiresInMillis: z.number().nullable(),
  refreshableInMillis: z.number().nullable(),
  version: z.string(),
})

async function parseResponse(res: Response, requestedAt: number) {
  const status = res.status
  const dateHeader = res.headers.get('date')
  if (!(status < 300 || status === 401 || status === 403)) {
    throw new AppError({
      message: res.statusText,
      HTTPStatus: status,
    })
  }
  if (!dateHeader) {
    throw new AppError({
      message: 'missing date header',
    })
  }

  const data = await res.json()
  const responseData = AuthStatusSchema.parse(data)
  return {
    serverTimeMillis: Date.parse(dateHeader),
    ...responseData,
    // Subtract the full request duration conservatively: transit time is unknown.
    // A renewal slower than the token's lifetime counts as expired; the next
    // request checks again.
    // An unknown lifetime trusts the token: the timed refresh and the 401
    // retry still cover a token that has really expired.
    tokenExpiresAt:
      responseData.expiresInMillis === null
        ? Number.POSITIVE_INFINITY
        : requestedAt + responseData.expiresInMillis,
  }
}

async function apiRefresh() {
  const log = debug.extend('/api/refresh')
  log('request')
  const requestedAt = Date.now()
  const res = await authFetch('/api/refresh')
  // The cookie this response set; checkStatus asks again if another tab
  // changes it before the status is used.
  const accessToken = cookieToken()
  log('response', res)

  const status = await parseResponse(res, requestedAt)
  return { ...status, renewed: true, accessToken }
}

async function apiStatus() {
  const log = debug.extend('/api/status')
  try {
    log('request')
    const requestedAt = Date.now()
    const res = await authFetch('/api/status')
    log('response', res)

    return {
      ...(await parseResponse(res, requestedAt)),
      renewed: false,
      accessToken: cookieToken(),
    }
  } catch (err) {
    // Something went wrong, try refresh
    log('response', err)
    return apiRefresh()
  }
}

/** The token in the access_token cookie, which every tab shares. */
function cookieToken(): string {
  const cookie = cookies.get('access_token')
  return typeof cookie === 'string' ? cookie : ''
}

/** Status checks to try while other tabs keep changing the cookie. */
const statusAttempts = 3

/**
 * Asks /api/status, renewing first when the server says the token is
 * refreshable. A status check describes the cookie the request carried, and
 * the token cached with it is read from the cookie afterwards: if another
 * tab changed the cookie in between (renewed, signed out, switched
 * account), the answer may belong to the previous token, so it is asked
 * again.
 */
async function checkStatus() {
  for (let attempt = 1; ; attempt++) {
    const sent = cookieToken()
    let status = await apiStatus()
    if (status.refreshable && !status.renewed) {
      status = await apiRefresh()
    }
    const accessToken = status.accessToken
    if (
      cookieToken() === accessToken &&
      (status.renewed || accessToken === sent)
    ) {
      return { status, accessToken }
    }
    if (attempt === statusAttempts) {
      throw new AppError({
        message: 'The access token kept changing during the status check',
      })
    }
    debug('access_token changed during the status check, asking again')
  }
}

let refreshTimeoutId = 0

function scheduleTimedRefresh(delay: number) {
  window.clearTimeout(refreshTimeoutId)
  refreshTimeoutId = window.setTimeout(() => {
    refetchAuthStatus().catch(error => {
      debug('Timed token refresh failed, retrying later', error)
      scheduleTimedRefresh(timedRefreshRetryMillis)
    })
  }, delay)
}

/**
 * The status check (and renewal, when due) in progress. Every caller (the
 * auth status query, the timed refresh, requests checking their token, and
 * requests rejected as unauthenticated) shares it until it settles, so
 * parallel renewals never race for the same refresh token.
 */
let pendingAuthStatus: Promise<ApiStatusType> | undefined

/** Loads the auth status, sharing the request in progress. */
function loadAuthStatus(): Promise<ApiStatusType> {
  pendingAuthStatus ??= fetchAuthStatus().finally(() => {
    pendingAuthStatus = undefined
  })
  return pendingAuthStatus
}

/**
 * Loads the auth status outside the query and caches it on success. A
 * failure only rejects for the caller: it must not put the shared auth
 * status query into an error state, since every component reading it
 * would throw to the error page.
 */
async function refetchAuthStatus(): Promise<ApiStatusType> {
  const status = await loadAuthStatus()
  queryClient.setQueryData(AuthStatusKey, status)
  return status
}

async function fetchAuthStatus(): Promise<{
  authenticated: boolean
  accessToken: string
  username: string | null
  serverTimeMillis: number
  version: string
  tokenExpiresAt: number
}> {
  const log = debug.extend('getOrRefreshApiStatus')
  // Renews only when the server says the token is due: /api/refresh returns
  // the same token otherwise, and a refused refresh marks the user logged out.
  const { status, accessToken } = await checkStatus()

  const {
    authenticated,
    username,
    refreshableInMillis: refreshInMillis,
    serverTimeMillis,
    version,
    tokenExpiresAt,
  } = status
  if (typeof refreshInMillis === 'number' && refreshInMillis > 0) {
    const timeout = refreshInMillis + Math.round(Math.random() * 10_000)
    log(`Setting timeout for token refresh in ${timeout}ms`)
    scheduleTimedRefresh(timeout)
  } else {
    window.clearTimeout(refreshTimeoutId)
    refreshTimeoutId = 0
  }

  return {
    authenticated,
    accessToken,
    username,
    serverTimeMillis,
    version,
    tokenExpiresAt,
  }
}

type ApiStatusType = Awaited<ReturnType<typeof fetchAuthStatus>>

const configQueryOptions = {
  retry: true, // don't fail and display error page by never giving up resolving the auth status
  queryKey: AuthStatusKey,
  queryFn: () => loadAuthStatus(),
  placeholderData: keepPreviousData,
  refetchOnMount: false,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} satisfies UseQueryOptions<ApiStatusType>

export function useAuthStatus() {
  return useQuery(configQueryOptions)
}

export function useAuthStatusSuspense() {
  return useSuspenseQuery(configQueryOptions)
}

export function useAuthenticated() {
  return asTrilean(useAuthStatus().data?.authenticated)
}

export function ensureAuthStatus() {
  return queryClient.ensureQueryData(configQueryOptions)
}

export function getAuthStatus() {
  return queryClient.getQueryData<ApiStatusType>(AuthStatusKey)
}

export function getAccessToken() {
  return getAuthStatus()?.accessToken
}

export async function isAuthenticated() {
  return (await ensureAuthStatus()).authenticated
}

function hasValidToken(status: ApiStatusType | undefined, buffer = 0) {
  return !!(
    status?.authenticated &&
    status.accessToken &&
    status.tokenExpiresAt > Date.now() + buffer
  )
}

/** The status was loaded within the reuse window. */
function isRecentStatus() {
  const updatedAt = queryClient.getQueryState(AuthStatusKey)?.dataUpdatedAt
  return !!updatedAt && Date.now() - updatedAt < statusReuseMillis
}

/**
 * The token of `status`, or '' when it has none, it has expired, or it
 * belongs to another user than `before` (another tab switched account): an
 * operation must not continue as someone else.
 */
function tokenFor(before: ApiStatusType | undefined, status: ApiStatusType) {
  if (before?.authenticated && status.username !== before.username) {
    return ''
  }
  return hasValidToken(status) ? status.accessToken : ''
}

/**
 * The user a JWT access token belongs to (its `username` claim, else `sub`),
 * or undefined when the token can't be read. Only compares tokens; the
 * signature is the server's business.
 */
function tokenPrincipal(token: string): string | undefined {
  const payload = token.split('.')[1]
  if (!payload) {
    return undefined
  }
  try {
    const claims: unknown = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    )
    if (!claims || typeof claims !== 'object') {
      return undefined
    }
    const { username, sub } = claims as { username?: unknown; sub?: unknown }
    if (typeof username === 'string' && username) {
      return username
    }
    return typeof sub === 'string' && sub ? sub : undefined
  } catch {
    return undefined
  }
}

/**
 * The access token to retry a call with after the server rejected it as
 * unauthenticated (the timed refresh can fire late, e.g. after sleep). Empty
 * when there is no usable token for the user the rejected token belongs to.
 *
 * A token that already replaced `rejectedToken` (another request or a status
 * check renewed it meanwhile) is returned without asking again. Otherwise
 * the status is checked, sharing a check in progress, which renews the token
 * when the server says it is due. It may return `rejectedToken` itself when
 * the server still considers it valid.
 */
export async function forceRefreshAccessToken(
  rejectedToken?: string,
): Promise<string> {
  const before = getAuthStatus()
  const cached =
    rejectedToken &&
    before &&
    before.accessToken !== rejectedToken &&
    hasValidToken(before)
      ? before.accessToken
      : ''
  const token = cached || tokenFor(before, await refetchAuthStatus())
  // The call was made as the user of the rejected token. The cookie is shared
  // by all tabs, so the current token may belong to another user.
  if (rejectedToken && token) {
    const user = tokenPrincipal(rejectedToken)
    if (!user || tokenPrincipal(token) !== user) {
      debug(
        "The current token is not for the rejected token's user: not retrying",
      )
      return ''
    }
  }
  return token
}

export function useAccessToken() {
  return useAuthStatus().data?.accessToken
}

export function useRefreshAccessToken() {
  return useMutation({ mutationFn: () => forceRefreshAccessToken() })
}

export async function getOrRefreshAccessToken() {
  const current = await ensureAuthStatus()
  let status = pendingAuthStatus
    ? await pendingAuthStatus.catch(() => current)
    : current

  // Another tab renewed the token, signed out or signed in (possibly as
  // someone else): the shared cookie no longer holds the cached token, so the
  // cached status must not be reused.
  const cookieChanged = cookieToken() !== status.accessToken
  // A recent answer that is logged out, or a valid token the server did not
  // renew yet, is used as is rather than asked for again on every request.
  const reusable =
    hasValidToken(status, refreshBufferMillis) ||
    (isRecentStatus() && (!status.authenticated || hasValidToken(status)))
  if (cookieChanged || !reusable) {
    status = await refetchAuthStatus()
  }

  return tokenFor(current, status)
}
